import type {
  CsvAnalyzeResponse,
  CsvParseResponse,
  LicenceResponse,
  ListResponse,
  ProfileResponse,
} from '@shared/schemas';
import { buffers, END, type EventChannel, eventChannel } from 'redux-saga';
import {
  all,
  call,
  debounce,
  getContext,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';
import { classifyInfraError } from '@/utils/infra-error';

import { buildEnrichmentStats } from '../../_shared/domain/enrichment';
import type { FileUploadState } from '../../_shared/domain/file-import.model';
import { toListWizardError } from '../../_shared/domain/list-error';
import { buildSavePlan } from '../../_shared/domain/save-plan';
import type { UploadProgress } from '../../_shared/infrastructure/file-import-api';
import type { ListDraft, ProfileSelection } from '../domain/list-draft.model';
import { validateDraft } from '../domain/list-draft.validation';
import { keepSelected, type RowSelection } from '../domain/row-selection';
import type {
  AddListGateway,
  BucketsRaw,
  ImportDuplicates,
  SourceListOrigin,
} from '../infrastructure/add-list.gateway';
import {
  bucketsRequested,
  createEmptyRequested,
  fileAnalyzeRequested,
  fileParseRequested,
  fileUploadCancelRequested,
  importPreviewRequested,
  licenceCreated,
  licencesRequested,
  listImportRequested,
  scopeAnalyzeRequested,
  sourceListsRequested,
  submitCancelled,
  submitRequested,
} from './add-list.actions';
import {
  selectDraft,
  selectExcludeDuplicates,
  selectFileImportData,
  selectFileUpload,
  selectImportedProfileIds,
  selectImportPreviewPage,
  selectImportPreviewPageSize,
  selectLicences,
  selectProfilePage,
  selectProfilePageSize,
  selectProfileTotal,
  selectRowSelection,
  selectServerFilters,
  selectSourceLicenceId,
  selectSourceOrigin,
} from './add-list.selectors';
import { addListActions } from './add-list.slice';

/**
 * Core save orchestration: create → (import) → (associate) → (enrich). Each
 * optional step is decided by the pure `buildSavePlan`. All I/O goes through the
 * injected gateway; errors are mapped to domain errors and surfaced via
 * `saveFailed`. Dispatches `saveSucceeded` on completion.
 */
function* runSave(selection: ProfileSelection, draft: ListDraft) {
  const gateway: AddListGateway = yield getContext('addListGateway');
  const plan = buildSavePlan(draft, selection);
  try {
    yield put(addListActions.savePhaseSet('Creating list…'));
    const list = (yield call(gateway.createList, draft)) as ListResponse;

    let createdIds: string[] = [];
    if (plan.fileImport) {
      yield put(addListActions.savePhaseSet('Importing profiles from file…'));
      createdIds = (yield call(gateway.importFile, list.id, plan.fileImport)) as string[];
    }
    if (plan.finalIds.length > 0) {
      yield put(addListActions.savePhaseSet('Associating profiles…'));
      yield call(gateway.associateProfiles, list.id, plan.finalIds);
    }
    // The file's new profiles only have an id now (docs/product-issues.md PI-115).
    const enrichIds = [...plan.enrichTargetIds, ...(plan.enrichFileCreated ? createdIds : [])];
    if (enrichIds.length > 0) {
      yield put(addListActions.savePhaseSet('Queueing enrichment session…'));
      yield call(gateway.queueEnrichment, list.id, draft.licenceId, enrichIds);
    }
    yield put(addListActions.saveSucceeded(list.id));
  } catch (e) {
    yield put(addListActions.saveFailed(toListWizardError(e)));
  }
}

/** Validate the draft up front; returns true when the flow may proceed. */
function* validateOrFail(draft: ListDraft) {
  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(addListActions.saveFailed({ kind: 'validation', fields: errors }));
    return false;
  }
  return true;
}

/**
 * WORKER: full save. Validates, then races the save against an explicit
 * `submitCancelled`. If cancellation wins, the save branch is auto-cancelled and
 * we flag it. (A fresh `submitRequested` also cancels this worker via
 * `takeLatest`; that supersession is silent: the new run governs the state.)
 */
function* saveWorker(action: ReturnType<typeof submitRequested>) {
  const draft = (yield select(selectDraft)) as ListDraft;
  if (!((yield call(validateOrFail, draft)) as boolean)) return;

  yield put(addListActions.saveStarted());
  const outcome: { cancelled?: unknown } = yield race({
    done: call(runSave, action.payload, draft),
    cancelled: take(submitCancelled.type),
  });
  if (outcome.cancelled !== undefined) {
    yield put(addListActions.saveFailed({ kind: 'cancelled' }));
  }
}

/** WORKER: create an empty list (no profiles). Reuses the same save pipeline. */
function* createEmptyWorker() {
  const draft = (yield select(selectDraft)) as ListDraft;
  if (!((yield call(validateOrFail, draft)) as boolean)) return;

  yield put(addListActions.saveStarted());
  yield call(
    runSave,
    { filterIds: [], importedIds: [], fileImport: null, enrichTargetIds: [] },
    draft,
  );
}

// ── Profile-selection data layer ──
//
// The debounce that used to live inside `useAdvancedFilters` (500ms) now lives
// here: a filter change waits, then resets to page 0, which is the single
// trigger for a fetch. Direct pagination fetches immediately.

/** All actions that change the pruned filter set (⇒ page reset + refetch). */
const FILTER_CHANGE_TYPES = [
  addListActions.ruleAdded.type,
  addListActions.ruleUpdated.type,
  addListActions.nodeRemoved.type,
  addListActions.groupAdded.type,
  addListActions.combinatorSet.type,
  addListActions.nodeMoved.type,
  addListActions.filtersCleared.type,
  addListActions.filtersLoaded.type,
  addListActions.excludeDuplicatesToggled.type,
];

/**
 * WORKER: a filter changed: reset to the first page. `pageChanged` is the sole
 * fetch trigger, so this dispatch (even to the same page 0) drives exactly one
 * refetch through `fetchProfilesWorker`.
 */
function* resetToFirstPageWorker() {
  yield put(addListActions.pageChanged(0));
}

/** WORKER: fetch one page of profiles for the current filters/page. Cancellable. */
function* fetchProfilesWorker() {
  const gateway: AddListGateway = yield getContext('addListGateway');
  const filters = (yield select(selectServerFilters)) as FilterGroup | null;
  const page = (yield select(selectProfilePage)) as number;
  const pageSize = (yield select(selectProfilePageSize)) as number;
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  const draft = (yield select(selectDraft)) as ListDraft;

  yield put(addListActions.profilesLoading());
  try {
    const { rows, total } = (yield call(gateway.fetchProfiles, {
      filters,
      page,
      pageSize,
      excludeDuplicates,
      licenceId: draft.licenceId || undefined,
    })) as { rows: ProfileResponse[]; total: number };
    yield put(addListActions.profilesLoaded({ rows, total }));
  } catch {
    // Non-blocking: clear the spinner, keep the previous rows.
    yield put(addListActions.profilesLoaded({ rows: [], total: 0 }));
  }
}

/** WORKER: recompute the live duplicate count (raw total minus filtered total). */
function* duplicateWorker() {
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  if (!excludeDuplicates) {
    yield put(addListActions.duplicateCountSet(0));
    return;
  }
  const gateway: AddListGateway = yield getContext('addListGateway');
  const filters = (yield select(selectServerFilters)) as FilterGroup | null;

  yield put(addListActions.duplicateLoadingStarted());
  try {
    const rawTotal = (yield call(gateway.countRawTotal, filters)) as number;
    const filteredTotal = (yield select(selectProfileTotal)) as number;
    yield put(addListActions.duplicateCountSet(Math.max(0, rawTotal - filteredTotal)));
  } catch {
    yield put(addListActions.duplicateCountSet(0));
  }
}

/** WORKER: fetch + segment the enrichment buckets for the confirm step. */
function* bucketsWorker() {
  const gateway: AddListGateway = yield getContext('addListGateway');
  yield put(addListActions.bucketsLoading());

  const filters = (yield select(selectServerFilters)) as FilterGroup | null;
  const excludeDuplicates = (yield select(selectExcludeDuplicates)) as boolean;
  const importedProfileIds = (yield select(selectImportedProfileIds)) as string[];
  const fileImportData = (yield select(selectFileImportData)) as {
    analyzeResult?: { existing_profile_ids?: string[] };
  } | null;
  const draft = (yield select(selectDraft)) as ListDraft;

  const fileExistingIds = fileImportData?.analyzeResult?.existing_profile_ids ?? [];
  const importedIds = [...importedProfileIds, ...fileExistingIds];

  // The ticked rows (board E3): one by one, they are read by id; "all results"
  // is the filter fetch it always was, less the rows unticked since.
  const selection = (yield select(selectRowSelection)) as RowSelection;
  const picking = draft.methods.includes('select');
  const tickedIds = picking && selection.mode === 'ids' ? selection.ids : [];

  try {
    const raw = (yield call(gateway.fetchForBuckets, {
      filters,
      excludeDuplicates,
      importedIds: [...importedIds, ...tickedIds],
      licenceId: draft.licenceId || undefined,
      includeFilterResults: picking && selection.mode === 'all',
    })) as BucketsRaw;
    const ticked = new Set(tickedIds);
    const imported = new Set(importedIds);
    const filterProfiles =
      selection.mode === 'all'
        ? keepSelected(raw.filterProfiles, selection)
        : raw.importedProfiles.filter((p) => ticked.has(p.id));
    // A ticked row read alongside the imports is not an import, unless it is both.
    const importedProfiles = raw.importedProfiles.filter(
      (p) => !ticked.has(p.id) || imported.has(p.id),
    );
    const stats = buildEnrichmentStats(
      filterProfiles,
      // The unticked rows are left out, not duplicates: keep them out of that count.
      raw.rawTotal - (raw.filterProfiles.length - filterProfiles.length),
      importedProfiles,
      excludeDuplicates,
    );
    yield put(addListActions.bucketsLoaded(stats));
  } catch {
    // Not three zeros: the step says it failed and offers a retry, and the list
    // cannot be created meanwhile; its selected profiles come from this (#621).
    yield put(addListActions.bucketsFailed());
  }
}

/** WORKER: analyse the scope (or the AI bar's text) into a filter tree, then load it. */
function* nlSearchWorker(action: ReturnType<typeof scopeAnalyzeRequested>) {
  const draft = (yield select(selectDraft)) as ListDraft;
  const trimmed = (action.payload ?? draft.scope).trim();
  if (!trimmed) return;

  const gateway: AddListGateway = yield getContext('addListGateway');
  yield put(addListActions.nlSearchStarted());
  try {
    const group = (yield call(gateway.analyzeScope, trimmed)) as FilterGroup;
    yield put(addListActions.filtersLoaded(group));
  } catch {
    // Non-blocking; the user keeps their current filters.
  } finally {
    yield put(addListActions.nlSearchEnded());
  }
}

type UploadEvent =
  | { progress: UploadProgress }
  | { result: CsvParseResponse }
  | { error: unknown };

/**
 * The upload as a stream of events: bytes sent, then the parse result or the
 * error. Closing the channel aborts the request; that is how Cancel works.
 */
function uploadChannel(gateway: AddListGateway, file: File) {
  return eventChannel<UploadEvent>((emit) => {
    const controller = new AbortController();
    gateway
      .parseFile(file, { onProgress: (progress) => emit({ progress }), signal: controller.signal })
      .then(
        (result) => emit({ result }),
        (error: unknown) => emit({ error }),
      )
      .finally(() => emit(END));
    return () => controller.abort();
    // Buffered: an event emitted before the worker takes it is kept, not dropped.
  }, buffers.expanding<UploadEvent>(10));
}

/** WORKER: send and parse the file dropped on the embedded import step, reporting the bytes sent. */
function* fileParseWorker(action: ReturnType<typeof fileParseRequested>) {
  const gateway: AddListGateway = yield getContext('addListGateway');
  yield put(
    addListActions.fileParseStarted({ fileName: action.payload.name, fileSize: action.payload.size }),
  );
  const channel: EventChannel<UploadEvent> = yield call(uploadChannel, gateway, action.payload);
  try {
    while (true) {
      const { event, cancelled } = (yield race({
        event: take(channel),
        cancelled: take(fileUploadCancelRequested.type),
      })) as { event?: UploadEvent; cancelled?: unknown };
      if (cancelled !== undefined) {
        yield put(addListActions.fileParseCancelled());
        return;
      }
      if (!event) return;
      if ('progress' in event) {
        yield put(addListActions.fileUploadProgressed(event.progress));
      } else if ('result' in event) {
        const result = event.result;
        // A file the backend could not read at all: surface the first error and
        // stay on the Upload sub-step (parity with the original stepper).
        if (result.errors.length > 0 && result.row_count === 0) {
          yield put(
            addListActions.fileParseFailed({ kind: 'api', message: result.errors[0], status: 0 }),
          );
          return;
        }
        yield put(addListActions.fileParseSucceeded(result));
        return;
      } else {
        yield put(
          addListActions.fileParseFailed(classifyInfraError(event.error, 'Failed to parse file')),
        );
        return;
      }
    }
  } finally {
    channel.close();
  }
}

/** WORKER: dry-run the mapped rows of the embedded import step. */
function* fileAnalyzeWorker() {
  const fileUpload = (yield select(selectFileUpload)) as FileUploadState;
  if (!fileUpload.parseResult) return; // the UI can't request this before a parse

  const gateway: AddListGateway = yield getContext('addListGateway');
  yield put(addListActions.fileAnalyzeStarted());
  try {
    const result = (yield call(gateway.analyzeRows, {
      column_mapping: fileUpload.columnMapping,
      rows: fileUpload.parseResult.rows,
    })) as CsvAnalyzeResponse;
    yield put(addListActions.fileAnalyzeSucceeded(result));
  } catch (e) {
    yield put(addListActions.fileAnalyzeFailed(classifyInfraError(e, 'Analysis failed')));
  }
}

/**
 * WORKER: load the licence dropdown options.
 *
 * A failure lands an empty dropdown and stays silent (no snackbar): this is
 * deliberate parity with the `useApi` version the step used, whose `error` the
 * UI never read. `takeLatest` replaces that hook's AbortController; a
 * superseded run is cancelled so its `put` never lands, though the in-flight
 * request itself is no longer aborted.
 */
function* licencesWorker() {
  const gateway: AddListGateway = yield getContext('addListGateway');
  yield put(addListActions.licencesLoadStarted());
  try {
    const rows = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(addListActions.licencesLoaded(rows));
  } catch {
    yield put(addListActions.licencesLoaded([]));
  }
}

/**
 * WORKER: a licence was created from the picker (#597): reload the licences so
 * it is on offer, then pick it; the same round trip as Add user (#593).
 */
function* licenceCreatedWorker(action: ReturnType<typeof licenceCreated>) {
  yield call(licencesWorker);
  const licences = (yield select(selectLicences)) as LicenceResponse[];
  const created = licences.find((l) => l.id === action.payload);
  if (!created) return;
  yield put(addListActions.fieldChanged({ field: 'licenceId', value: created.id }));
  yield put(addListActions.fieldChanged({ field: 'licenceName', value: created.name }));
}

/** The actions that change the draft's licence; each needs that licence's lists. */
const isLicencePick = (action: { type: string; payload?: unknown }) =>
  (action.type === addListActions.fieldChanged.type &&
    (action.payload as { field?: string } | undefined)?.field === 'licenceId') ||
  (action.type === addListActions.draftHydrated.type &&
    Boolean((action.payload as { licenceId?: string } | undefined)?.licenceId));

/**
 * WORKER: the names of the picked licence's lists, for the next list number of
 * the suggested name (#595). Best-effort: a failure leaves the name to the admin
 * rather than suggesting a number that may already be taken.
 */
function* licenceListNamesWorker() {
  const gateway: AddListGateway = yield getContext('addListGateway');
  const { licenceId } = (yield select(selectDraft)) as ListDraft;
  if (!licenceId) {
    yield put(addListActions.licenceListNamesLoaded(null));
    return;
  }
  try {
    const lists = (yield call(gateway.fetchSourceLists, {
      origin: 'all',
      licenceId,
    })) as ListResponse[];
    yield put(addListActions.licenceListNamesLoaded(lists.map((l) => l.name)));
  } catch {
    yield put(addListActions.licenceListNamesLoaded(null));
  }
}


/**
 * WORKER: load the source-list dropdown. Reads origin + licence from the slice
 * (they are the request's parameters). `takeLatest` on the three triggering
 * actions replaces the old `useApi` abort-on-path-change: a superseded run is
 * cancelled so a stale response can never overwrite a newer one. A failure lands
 * an empty dropdown, silently.
 */
function* sourceListsWorker() {
  const gateway: AddListGateway = yield getContext('addListGateway');
  const origin = (yield select(selectSourceOrigin)) as SourceListOrigin;
  const licenceId = (yield select(selectSourceLicenceId)) as string;

  yield put(addListActions.sourceListsLoadStarted());
  try {
    const rows = (yield call(gateway.fetchSourceLists, {
      origin,
      ...(licenceId ? { licenceId } : {}),
    })) as ListResponse[];
    yield put(addListActions.sourceListsLoaded(rows));
  } catch {
    yield put(addListActions.sourceListsLoaded([]));
  }
}

/**
 * WORKER: import every profile of the chosen source list.
 *
 * The duplicate check is best-effort: if it fails, the import still goes through
 * with ALL ids and no duplicates reported, exactly as the old step did. It is
 * skipped entirely when there is no target licence or nothing to check. A
 * failure of the id fetch itself is silent; the button just re-enables.
 */
function* listImportWorker(action: ReturnType<typeof listImportRequested>) {
  const { listId, listName } = action.payload;
  const gateway: AddListGateway = yield getContext('addListGateway');
  const draft = (yield select(selectDraft)) as { licenceId: string };

  yield put(addListActions.listImportStarted());
  try {
    const rawIds = (yield call(gateway.fetchListProfileIds, listId)) as string[];

    let duplicateIds: string[] = [];
    if (draft.licenceId && rawIds.length > 0) {
      try {
        const dup = (yield call(
          gateway.checkImportDuplicates,
          rawIds,
          draft.licenceId,
        )) as ImportDuplicates;
        duplicateIds = dup.duplicateIds;
      } catch {
        duplicateIds = [];
      }
    }

    yield put(addListActions.listImportSucceeded({ rawIds, duplicateIds, listName }));
  } catch {
    yield put(addListActions.listImportFailed());
  }
}

/**
 * WORKER: fetch one page of the imported-profiles preview. Ids/page/pageSize
 * are read from the slice; an empty selection settles to an empty table without
 * a request. Silent on failure (clears the spinner), matching the old hook.
 */
function* importPreviewWorker() {
  const ids = (yield select(selectImportedProfileIds)) as string[];
  if (ids.length === 0) {
    yield put(addListActions.importPreviewLoaded({ rows: [], total: 0 }));
    return;
  }
  const gateway: AddListGateway = yield getContext('addListGateway');
  const page = (yield select(selectImportPreviewPage)) as number;
  const pageSize = (yield select(selectImportPreviewPageSize)) as number;

  yield put(addListActions.importPreviewLoading());
  try {
    const { rows, total } = (yield call(gateway.fetchProfilesByIds, ids, page, pageSize)) as {
      rows: ProfileResponse[];
      total: number;
    };
    yield put(addListActions.importPreviewLoaded({ rows, total }));
  } catch {
    yield put(addListActions.importPreviewLoaded({ rows: [], total: 0 }));
  }
}

/** WATCHERS. */
export function* addListSaga() {
  yield all([
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(createEmptyRequested.type, createEmptyWorker),
    // Profile table: filter change → 500ms → reset to page 0 → fetch.
    debounce(500, FILTER_CHANGE_TYPES, resetToFirstPageWorker),
    takeLatest(
      [addListActions.pageChanged.type, addListActions.pageSizeChanged.type],
      fetchProfilesWorker,
    ),
    // Live duplicate count: 400ms debounce on the same filter changes.
    debounce(400, FILTER_CHANGE_TYPES, duplicateWorker),
    // Enrichment buckets + NL scope search (explicit signals from the UI).
    takeLatest(bucketsRequested.type, bucketsWorker),
    takeLatest(scopeAnalyzeRequested.type, nlSearchWorker),
    // Embedded file-import flow.
    takeLatest(fileParseRequested.type, fileParseWorker),
    takeLatest(fileAnalyzeRequested.type, fileAnalyzeWorker),
    // Licence dropdown of the details step.
    takeLatest(licencesRequested.type, licencesWorker),
    takeLatest(licenceCreated.type, licenceCreatedWorker),
    // Name suggestion: the picked licence's lists give the next number.
    takeLatest(isLicencePick, licenceListNamesWorker),
    // Import-from-list step: dropdown reload is the abort-on-change replacement.
    takeLatest(
      [
        sourceListsRequested.type,
        addListActions.sourceOriginChanged.type,
        addListActions.sourceLicenceChanged.type,
      ],
      sourceListsWorker,
    ),
    takeLatest(listImportRequested.type, listImportWorker),
    // Imported-profiles preview: refetch on import / exclusion toggle / pagination.
    takeLatest(importPreviewRequested.type, importPreviewWorker),
  ]);
}
